import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { userAgentUtil } from '@figure/frontlib-util';
import { Button, Modal, Width, Error, Legal, WebcamContainer } from '@figure/frontlib-gui';
import { useTelemetry } from 'shared/hooks';
import { FormErrorHandler } from 'shared/components';
import infoImage from 'shared/assets/images/info.svg';
import webcamGuideImg from 'shared/assets/images/webcam-guide.svg';

export const StyledForm = styled.div`
  .webcam-video-container {
    @media print, screen and (min-width: ${Width.MD}px) {
      max-height: 512px;
      overflow: hidden;
    }
    @media print, screen and (min-width: ${Width.LG}px) {
      max-height: 550px;
    }
  }
  .webcam-content {
    padding-top: 16px;
    padding-bottom: 16px;
    @media print, screen and (min-width: ${Width.SM}px) {
      header {
        margin-bottom: 40px;
      }
    }
    @media print, screen and (min-width: ${Width.MD}px) {
      button {
        margin-top: 32px;
      }
    }
  }
`;

const LegalText = styled(Legal)`
  @media print, screen and (min-width: ${Width.MD}px) {
    margin-top: 32px;
  }
`;

const StyledInfoIcon = styled.div`
  width: 45px;
  height: 45px;
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 2;
  border-radius: 45px;
  background: url(${infoImage}) center no-repeat;

  @media print, screen and (min-width: ${Width.MD}px) {
    bottom: 48px;
    right: 48px;
  }
`;

const IDImageForm = ({ image, serverError, handleSubmit, handleResetPhoto }) => {
  const { telemetry } = useTelemetry();
  const handleFormSubmit = (data) => {
    telemetry('plIdUploaded');
    handleSubmit(data);
  };

  return (
    <Formik
      initialValues={{ image: image || '' }}
      validationSchema={yup.lazy(() => yup.object().shape({ image: yup.string().required() }))}
      onSubmit={handleFormSubmit}
    >
      {({ errors, touched, handleSubmit }) => (
        <>
          <FormErrorHandler errors={errors} touched={touched} serverError={serverError} />
          <Form onSubmit={handleSubmit}>
            <LegalText>
              By pressing “Save and continue”, you confirm that this is an accurate photo of your
              valid, government-issued ID or Passport.
            </LegalText>

            <Error display="block">{serverError}</Error>

            <div className="center" style={{ marginTop: '2rem' }}>
              <Button secondary onClick={handleResetPhoto} type="button" large autoFocus>
                Retake photo
              </Button>

              <Button onClick={handleSubmit} type="button" primary large>
                Save and continue
              </Button>
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
};

IDImageForm.propTypes = {
  image: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleResetPhoto: PropTypes.func.isRequired,
  serverError: PropTypes.string.isRequired,
};

const IDWebcamForm = ({ handleSubmit, serverError }) => {
  const [isOpen, setIsOpen] = useState(userAgentUtil.isMobileDevice());
  const [webcamImage, setWebcamImage] = useState({ previewImage: null, blob: null });

  const onClose = () => setIsOpen(false);
  const onOpen = () => setIsOpen(true);
  const onReset = () => setWebcamImage({ previewImage: null, blob: null });
  const onSubmit = (data) => {
    if (!webcamImage.blob) return;
    handleSubmit({ blob: webcamImage.blob, consent: data.consent });
  };
  return (
    <StyledForm>
      <WebcamContainer
        captureGuidelineOverlay={webcamGuideImg}
        handleScreenshotData={({ blob, image }) => setWebcamImage({ blob, previewImage: image })}
        previewImage={webcamImage.previewImage}
      />

      {webcamImage.previewImage && (
        <IDImageForm
          image={webcamImage.previewImage}
          handleResetPhoto={onReset}
          handleSubmit={onSubmit}
          serverError={serverError}
        />
      )}

      {!isOpen && !webcamImage.previewImage && userAgentUtil.isMobileDevice() && (
        <StyledInfoIcon onClick={onOpen} />
      )}

      <Modal
        header="Protecting your identity is important."
        size="sm"
        closable={false}
        isOpen={isOpen}
      >
        <p>
          Use your device camera to get a picture of your ID. Make sure you have good lighting and
          all four corners of your ID are visible in the picture.
        </p>

        <Button primary large center onClick={onClose}>
          Take a photo
        </Button>
      </Modal>
    </StyledForm>
  );
};

IDWebcamForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  serverError: PropTypes.string.isRequired,
};

export default IDWebcamForm;
